import { Link } from 'react-router-dom';

import { useSite } from '../../app/site';
import { useLocale } from '../../lib/useLocale';
import { cn } from '../../lib/cn';
import { LogoMark } from './Logo';
import { SiteNavAnchor } from './SiteNavAnchor';

export function Footer() {
  const { t, text } = useLocale();
  const { settings, features } = useSite();
  const siteName = settings?.siteName ?? t('common.appName');
  const footerLinks = settings?.navLinks.footer ?? [];
  const documents = settings?.legal.documents ?? [];
  const legalLinks =
    documents.length > 0
      ? [
          { to: '/guidelines', label: t('guidelines.link') },
          ...documents.map((doc) => ({
            to: `/terms/${doc.code}`,
            label: text(doc.name) || doc.nameLocalized || t('terms.link'),
          })),
        ]
      : [
          { to: '/guidelines', label: t('guidelines.link') },
          { to: '/terms', label: t('terms.link') },
        ];

  const columns = [
    {
      title: t('nav.home'),
      links: [
        { to: '/achievements', label: t('nav.achievements') },
        { to: '/leaderboard', label: t('nav.leaderboard') },
        ...(features.community ? [{ to: '/community', label: t('nav.community') }] : []),
        { to: '/players', label: t('nav.players') },
      ],
    },
    {
      title: t('nav.profile'),
      links: [
        { to: '/speedruns', label: t('nav.speedruns') },
        { to: '/points', label: t('nav.points') },
        { to: '/notifications', label: t('nav.notifications') },
        { to: '/settings', label: t('nav.settings') },
      ],
    },
    {
      title: text(settings?.legal.columnTitle) || t('terms.column'),
      links: legalLinks,
    },
  ];

  return (
    <footer className="border-line bg-surface mt-20 border-t">
          <div
            className={cn(
              'max-w-8xl mx-auto grid gap-10 px-4 py-12 sm:px-6 lg:px-8',
              footerLinks.length > 0 ? 'lg:grid-cols-[1.5fr_1fr_1fr_1fr_1fr]' : 'lg:grid-cols-[1.5fr_1fr_1fr_1fr]',
            )}
          >
        <div className="max-w-sm">
          <div className="flex items-center gap-2.5">
            <LogoMark className="size-7" />
            <span className="font-display text-sm font-semibold">{siteName}</span>
          </div>
          <p className="text-text-subtle mt-3 text-sm">
            {settings?.siteDescription || t('home.subtitle')}
          </p>
        </div>
        {columns.map((column) => (
          <div key={column.title}>
            <p className="eyebrow mb-3">{column.title}</p>
            <ul className="space-y-2">
              {column.links.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-text-subtle hover:text-text text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {footerLinks.length > 0 ? (
          <div>
            <p className="eyebrow mb-3">{t('nav.links')}</p>
            <ul className="space-y-2">
              {footerLinks.map((link) => (
                <li key={link.code || link.id}>
                  <SiteNavAnchor
                    href={link.url}
                    className="text-text-subtle hover:text-text text-sm transition-colors"
                  >
                    {text(link.name) || link.nameLocalized}
                  </SiteNavAnchor>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
      <div className="border-line border-t px-4 py-5 sm:px-6 lg:px-8">
        <div className="max-w-8xl text-2xs text-text-faint mx-auto flex flex-wrap items-center justify-between gap-3">
          <p>© {new Date().getFullYear()} Medaka Games</p>
          <p className="numeric">Medaka Games Reward · player platform</p>
        </div>
      </div>
    </footer>
  );
}
